/**
 * 「熔暮绸缎」shader 背景的调色板预设 (配置文件渠道 —— 改这里要重新 build 部署)。
 *
 * 三套主题共享同一个 fragment shader, 只换 5 锚点 ramp + 调参 (经 uniform 喂入), 切主题时 JS lerp 平滑过渡。
 * 设计师增/改一套调色板就改这个文件 (无需碰 GLSL)。访客在前台 /settings 选用, 站长在后台设默认 (P1)。
 *
 * 字段:
 * - anchors: 5 锚点 (暗→亮) 渐变色; **anchors[0] 必须够暗** (全站奶油色文字压在背景上, 暗底才可读)
 * - base: 底部渐隐目标色 (通常 ≈ anchors[0])
 * - fieldLo/fieldHi: 基线亮度窗口 (越高画面越暗, 亮色只留峰值)
 * - exposure: 全局曝光乘子; ridgeGain: 金丝高光强度; maskDim: 中央阅读列压暗 (越小越暗, 保正文可读)
 * - sparkle: >0 = 常驻星屑微闪 (子夜专属)
 * - glow: 高光/点击涟漪光晕/长按充能/流星/金屑的点缀色 (替代旧硬编码暖金 GOLD); 暖色主题用金, polar 用青霓虹
 * - rimA/rimB: 点击涟漪环沿虹彩双色 (随波相位 rimB→glow→rimA 滚动); 暖色主题玫瑰↔珊瑚, polar 品红↔青
 *
 * 可读性契约 (新增套餐入库前设计师必须目检): anchors[0] 够暗 + maskDim 压暗到位 + ridge 金丝不过曝。
 */
// 暖色 (极光 aurora): dusk/dawn/night; 冷色 (极夜 polar): polar-dusk/polar-dawn/polar-night。
// bgMode(熔暮/破晓/子夜/自动) 与 themeSkin(aurora/polar) 正交 — App.vue 按 `polar-${bgMode档}` 选冷色版。
export type ShaderThemeName =
  | 'dusk'
  | 'dawn'
  | 'night'
  | 'polar-dusk'
  | 'polar-dawn'
  | 'polar-night';

export interface ShaderThemeDef {
  anchors: [string, string, string, string, string];
  base: string;
  fieldLo: number;
  fieldHi: number;
  exposure: number;
  ridgeGain: number;
  maskDim: number;
  sparkle: number;
  glow: string; // 高光/涟漪光晕/流星点缀色 (替代旧硬编码 GOLD)
  rimA: string; // 涟漪环沿虹彩外锚
  rimB: string; // 涟漪环沿虹彩内锚
}

export const SHADER_THEMES: Record<ShaderThemeName, ShaderThemeDef> = {
  // 熔暮 (默认): 深李子紫黑上的日落绸缎
  dusk: {
    anchors: ['#1a0b1f', '#6b2d8c', '#e85d75', '#ff7e5f', '#feb47b'],
    base: '#1a0b1f',
    fieldLo: 0.3,
    fieldHi: 1.04,
    exposure: 0.9,
    ridgeGain: 0.5,
    maskDim: 0.64,
    sparkle: 0,
    glow: '#ffd66b',
    rimA: '#e85d75',
    rimB: '#ff7d5e',
  },
  // 破晓 (亮): 晨雾初光 — ramp 较熔暮上移一档 (雾紫阴影核→紫藤晨雾→晨玫→桃乳→奶油金),
  // 亮在中高调氛围; 暗部锚点仍深 + 中央列压更狠 (0.45) 保奶油色文字可读; 微量金屑 = 穿雾晨光
  dawn: {
    anchors: ['#3b2752', '#8a5cb8', '#f08ca3', '#ffb397', '#ffe3ba'],
    base: '#3b2752',
    fieldLo: 0.18,
    fieldHi: 0.98,
    exposure: 1.0,
    ridgeGain: 0.42,
    maskDim: 0.45,
    sparkle: 0.12,
    glow: '#ffd66b',
    rimA: '#e85d75',
    rimB: '#ff7d5e',
  },
  // 子夜 (暗): 熔暮下压两档 — 近黑李子紫夜空, 暖色只在峰值成余烬细丝 (酒红→暗珊瑚→余烬桃),
  // exposure 收光 + ridgeGain 升 (黑底上金丝当主角) + 常驻星屑微闪
  night: {
    anchors: ['#08040e', '#2b1140', '#6f2547', '#a8473a', '#cf7a4e'],
    base: '#050208',
    fieldLo: 0.52,
    fieldHi: 1.18,
    exposure: 0.7,
    ridgeGain: 0.62,
    maskDim: 0.55,
    sparkle: 0.22,
    glow: '#ffd66b',
    rimA: '#e85d75',
    rimB: '#ff7d5e',
  },
  // ── 极夜 (polar) 冷色三档 — 对应暖色 dusk/dawn/night 的明暗气质, 换赛博霓虹冷色弧 ──
  // 共享冷色点缀 (glow 青 / rim 品红↔青); anchors[0] 都近黑保冷白文字可读。配 data-theme="polar" 整站皮肤。

  // 极夜·熔暮 (默认中调): 近黑深蓝→靛蓝→电蓝→青→霓虹品红
  'polar-dusk': {
    anchors: ['#05060f', '#0a1f4a', '#1b4dff', '#00e5ff', '#ff2bd6'],
    base: '#05060f',
    fieldLo: 0.4,
    fieldHi: 1.12,
    exposure: 0.8,
    ridgeGain: 0.55,
    maskDim: 0.6,
    sparkle: 0.34,
    glow: '#00e5ff',
    rimA: '#ff2bd6',
    rimB: '#2bd6ff',
  },
  // 极夜·破晓 (亮): ramp 上移一档 — 深海蓝→海蓝→天青→冰青→霜白, 亮在中高调; 中央列压更狠保可读
  'polar-dawn': {
    anchors: ['#0a1832', '#244e8c', '#2f8fc8', '#5fe0e5', '#c8ecff'],
    base: '#0a1832',
    fieldLo: 0.2,
    fieldHi: 1.0,
    exposure: 0.98,
    ridgeGain: 0.45,
    maskDim: 0.46,
    sparkle: 0.16,
    glow: '#5fe0e5',
    rimA: '#ff2bd6',
    rimB: '#2bd6ff',
  },
  // 极夜·子夜 (最暗): 近黑只剩霓虹余丝 — 近黑→深靛→电蓝→亮蓝→青; 收光 + 升 sparkle = 数字星屑
  'polar-night': {
    anchors: ['#02030a', '#0a1430', '#123a6e', '#1766b0', '#00cfe0'],
    base: '#02030a',
    fieldLo: 0.52,
    fieldHi: 1.18,
    exposure: 0.7,
    ridgeGain: 0.6,
    maskDim: 0.55,
    sparkle: 0.34,
    glow: '#00e5ff',
    rimA: '#ff2bd6',
    rimB: '#2bd6ff',
  },
};
